import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Target, Heart, Star, Award, Trophy, Zap, Crown, RefreshCw, Loader2,
  BookOpen, CheckCircle, Circle, Play, ArrowRight, Camera, MessageSquare,
  Handshake, UserPlus, Mail, Send, Download, Share2, Link2, Shield, BarChart3,
  Activity, Gift, Briefcase, Eye, TrendingUp, DollarSign, X, Upload
} from "lucide-react";

const BADGE_ICONS = {
  handshake: Handshake,
  users: Users,
  gift: Gift,
  crown: Crown
};

export function HubZone({
  user,
  onboardingStatus,
  leaderboard,
  leaderboardPeriod,
  suggestedConnections,
  referralData,
  activityDashboard,
  showMemberManagement,
  setShowMemberManagement,
  managedMembers,
  memberFilter,
  setMemberFilter,
  fetchLeaderboard,
  handleOnboardingTask,
  handleConnect,
  openProfileCard,
  handleSendProfileReminders,
  fetchManagedMembers,
  handleToggleTier,
  API,
  authFetch
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-emerald-500/10 to-neutral-900/50 border-emerald-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">Welcome to the Member Hub</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">1.</span>
                  <span><strong>Complete your Quick Start Roadmap</strong> - Click each task to get started and earn points</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">2.</span>
                  <span><strong>Climb the Leaderboard</strong> - Earn points by engaging: introductions, connections, pod activity</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold">3.</span>
                  <span><strong>Collect Badges</strong> - Unlock achievements as you participate in the community</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Onboarding Progress */}
        <Card className="lg:col-span-2 bg-neutral-800/50 border-neutral-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#39FF14]" />
              Your Quick Start Roadmap
            </CardTitle>
            <CardDescription className="text-neutral-400">
              Complete these steps in your first 14 days to maximize your community experience
            </CardDescription>
          </CardHeader>
          <CardContent>
            {onboardingStatus && (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-neutral-400">Progress</span>
                    <span className="text-sm font-medium text-[#39FF14]">{onboardingStatus.percentage}%</span>
                  </div>
                  <Progress value={onboardingStatus.percentage} className="h-2 bg-neutral-700" />
                </div>
                <div className="space-y-3">
                  {[
                    { key: "profile_completed", label: "Complete your profile", icon: Users },
                    { key: "photo_uploaded", label: "Upload a profile photo", icon: Camera },
                    { key: "introduction_posted", label: "Post your introduction", icon: MessageSquare },
                    { key: "first_connection_made", label: "Make your first connection", icon: Handshake },
                    { key: "pod_joined", label: "Join a mastermind pod", icon: Target },
                    { key: "collaboration_goal_set", label: "Set a collaboration goal", icon: Star }
                  ].map((item) => {
                    const Icon = item.icon;
                    const isComplete = onboardingStatus.checklist[item.key];
                    return (
                      <div
                        key={item.key}
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          isComplete ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-neutral-900"
                        }`}
                      >
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Circle className="w-5 h-5 text-neutral-500" />
                        )}
                        <Icon className={`w-4 h-4 ${isComplete ? "text-emerald-400" : "text-neutral-500"}`} />
                        <span className={isComplete ? "text-white" : "text-neutral-400"}>{item.label}</span>
                        {!isComplete && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto text-[#39FF14] hover:bg-[#39FF14]/10"
                            onClick={() => handleOnboardingTask(item.key)}
                            data-testid={`onboarding-task-${item.key}`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Welcome Card */}
        {/* Welcome Video Card */}
        <WelcomeVideoCard user={user} API={API} authFetch={authFetch} />

        {/* Google Review Prompt */}
        <Card className="bg-gradient-to-r from-amber-500/10 to-neutral-800 border-amber-500/20">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-neutral-300">Enjoying the community? Help us grow!</p>
              </div>
              <a
                href="https://g.page/r/Cea96_JGj2rSEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 rounded-lg text-sm font-medium transition-colors"
                data-testid="google-review-link"
              >
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                Leave a Review
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Referral Card */}
        {referralData && (
          <Card className="bg-gradient-to-r from-purple-900/30 to-neutral-800 border-purple-500/20">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <Share2 className="w-5 h-5 text-purple-400" />
                <div>
                  <h3 className="font-semibold text-white text-sm">Invite & Earn</h3>
                  <p className="text-xs text-neutral-400">Share your referral link</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-neutral-900 rounded-lg px-3 py-2 text-xs font-mono text-[#39FF14] truncate">{referralData.referral_url || referralData.code}</div>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(referralData.referral_url || `${window.location.origin}/register?ref=${referralData.code}`); }} className="text-neutral-400 hover:text-white">
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-400">{referralData.referral_count || 0} referral{(referralData.referral_count || 0) !== 1 ? "s" : ""}</span>
                <div className="flex gap-2">
                  {[{n: 3, label: "Free Month", tier: "bronze"}, {n: 10, label: "VIP Badge", tier: "vip"}, {n: 25, label: "Partner", tier: "partner"}].map(t => (
                    <span key={t.tier} className={`px-2 py-0.5 rounded-full ${(referralData.referral_count || 0) >= t.n ? "bg-[#39FF14]/20 text-[#39FF14]" : "bg-neutral-700 text-neutral-500"}`}>
                      {t.n}: {t.label}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consultant Admin Panel */}
        {user?.role === "consultant" && (
          <Card className="bg-gradient-to-br from-amber-900/30 to-neutral-800 border-amber-500/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Admin Actions</h3>
                  <p className="text-xs text-neutral-400">Consultant tools</p>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={handleSendProfileReminders}
                  className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 justify-start"
                  data-testid="send-profile-reminders-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Profile Update Reminders
                </Button>
                <Button
                  onClick={() => { setShowMemberManagement(!showMemberManagement); if (!managedMembers) fetchManagedMembers(); }}
                  className="w-full bg-[#39FF14]/10 hover:bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/30 justify-start"
                  data-testid="manage-members-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {showMemberManagement ? "Hide Member Management" : "Manage Members"}
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const res = await authFetch(`${API}/download/bsc-overview`);
                      if (res.ok) {
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "My_Biz_BSC_Platform_Overview.pdf";
                        a.click();
                        window.URL.revokeObjectURL(url);
                      }
                    } catch (_e) { /* ignore */ }
                  }}
                  className="w-full bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 justify-start"
                  data-testid="download-bsc-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download BSC Platform Overview (PDF)
                </Button>
                <Button
                  onClick={() => window.open("/pitch-deck", "_blank")}
                  className="w-full bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 justify-start"
                  data-testid="download-pitch-deck-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Open Sponsorship Pitch Deck
                </Button>
              </div>

              {/* Member Management Panel */}
              {showMemberManagement && (
                <div className="mt-4 pt-4 border-t border-amber-500/20" data-testid="member-management-panel">
                  {managedMembers ? (
                    <>
                      <div className="grid grid-cols-4 gap-2 mb-4">
                        {[
                          { label: "Total", value: managedMembers.stats?.total || 0, color: "white" },
                          { label: "Premium", value: managedMembers.stats?.premium || 0, color: "#39FF14" },
                          { label: "Free", value: managedMembers.stats?.basic || 0, color: "#f59e0b" },
                          { label: "Clients", value: managedMembers.stats?.clients || 0, color: "#60a5fa" }
                        ].map((s, i) => (
                          <div key={i} className="text-center p-2 bg-neutral-900 rounded-lg">
                            <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                            <p className="text-xs text-neutral-500">{s.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mb-3">
                        {["all", "basic", "premium"].map(f => (
                          <Button
                            key={f}
                            size="sm"
                            variant={memberFilter === f ? "default" : "outline"}
                            onClick={() => setMemberFilter(f)}
                            className={memberFilter === f
                              ? "bg-[#39FF14] text-black hover:bg-[#32e612] text-xs"
                              : "border-neutral-600 text-neutral-400 text-xs"}
                          >
                            {f === "all" ? "All" : f === "basic" ? "Free" : "Premium"}
                          </Button>
                        ))}
                        <Button size="sm" variant="ghost" onClick={fetchManagedMembers} className="ml-auto text-neutral-400 text-xs">
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="max-h-[320px] overflow-y-auto space-y-1 pr-1">
                        {(managedMembers.members || [])
                          .filter(m => memberFilter === "all" || m.membership_tier === memberFilter)
                          .map((m) => (
                          <div key={m.user_id} className="flex items-center gap-3 p-2 bg-neutral-900 rounded-lg hover:bg-neutral-800 transition-colors">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: "rgba(57,255,20,0.1)" }}>
                              {m.picture ? (
                                <img src={m.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <Users className="w-4 h-4 text-[#39FF14]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">{m.name || "Unnamed"}</p>
                              <p className="text-xs text-neutral-500 truncate">{m.email}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {m.role === "client" ? (
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">Client</Badge>
                              ) : m.membership_tier === "premium" ? (
                                <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 text-xs">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Premium
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-neutral-600 text-neutral-400 text-xs">Free</Badge>
                              )}
                              {m.role === "community_member" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleToggleTier(m.user_id, m.membership_tier, m.name)}
                                  className={`h-7 px-2 text-xs ${m.membership_tier === "premium"
                                    ? "text-red-400 hover:bg-red-500/10 hover:text-red-300"
                                    : "text-[#39FF14] hover:bg-[#39FF14]/10"}`}
                                  data-testid={`toggle-tier-${m.user_id}`}
                                >
                                  {m.membership_tier === "premium" ? "Revoke" : "Grant Premium"}
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-[#39FF14]" />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Suggested Connections */}
        <Card className="lg:col-span-3 bg-neutral-800/50 border-neutral-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#39FF14]" />
              Suggested Connections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {suggestedConnections.length > 0 ? suggestedConnections.map((member, idx) => (
                <div key={idx} className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                      <p className="font-medium text-white cursor-pointer hover:text-[#39FF14] transition-colors" onClick={() => openProfileCard(member.user_id)}>{member.name || "Member"}</p>
                      <p className="text-xs text-neutral-400">{member.business_description?.slice(0, 50) || "Business owner"}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32e612]"
                    onClick={() => handleConnect(member.user_id, member.name)}
                    data-testid={`connect-btn-${idx}`}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Connect
                  </Button>
                </div>
              )) : (
                <div className="col-span-3 text-center py-8 text-neutral-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Complete your profile to get personalized connection suggestions</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Community Leaderboard */}
        <Card className="lg:col-span-3 bg-neutral-800/50 border-neutral-700" data-testid="community-leaderboard">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Top Contributors
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={leaderboardPeriod === "month" ? "default" : "outline"}
                  onClick={() => fetchLeaderboard("month")}
                  className={leaderboardPeriod === "month"
                    ? "bg-[#39FF14] text-black hover:bg-[#32e612]"
                    : "border-neutral-600 text-neutral-400 hover:text-white"}
                >
                  This Month
                </Button>
                <Button
                  size="sm"
                  variant={leaderboardPeriod === "all_time" ? "default" : "outline"}
                  onClick={() => fetchLeaderboard("all_time")}
                  className={leaderboardPeriod === "all_time"
                    ? "bg-[#39FF14] text-black hover:bg-[#32e612]"
                    : "border-neutral-600 text-neutral-400 hover:text-white"}
                >
                  All Time
                </Button>
              </div>
            </div>
            <CardDescription className="text-neutral-400">
              Recognizing our most active community members
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard ? (
              <div className="space-y-6">
                {/* My Stats */}
                <div className="p-4 bg-gradient-to-r from-[#39FF14]/10 to-neutral-900 rounded-lg border border-[#39FF14]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#39FF14]/20 rounded-full flex items-center justify-center">
                        <Zap className="w-5 h-5 text-[#39FF14]" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-400">Your Rank</p>
                        <p className="text-xl font-bold text-white">#{leaderboard.my_stats?.rank || "-"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-neutral-400">Your Points</p>
                      <p className="text-xl font-bold text-[#39FF14]">{leaderboard.my_stats?.points || 0}</p>
                    </div>
                    <div className="flex gap-2">
                      {leaderboard.my_stats?.badge_details?.map((badge) => {
                        const BadgeIcon = BADGE_ICONS[badge.icon] || Award;
                        return (
                          <div
                            key={badge.id}
                            className={`w-8 h-8 bg-${badge.color}-500/20 rounded-full flex items-center justify-center`}
                            title={badge.name}
                          >
                            <BadgeIcon className={`w-4 h-4 text-${badge.color}-400`} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Leaderboard */}
                <div className="space-y-3">
                  {leaderboard.leaders?.length > 0 ? leaderboard.leaders.map((leader, idx) => (
                    <div
                      key={leader.user_id}
                      className={`flex items-center gap-4 p-3 rounded-lg ${
                        idx === 0 ? "bg-amber-500/10 border border-amber-500/20" :
                        idx === 1 ? "bg-neutral-700/30 border border-neutral-600" :
                        idx === 2 ? "bg-orange-900/20 border border-orange-700/20" :
                        "bg-neutral-900"
                      }`}
                      data-testid={`leaderboard-rank-${idx + 1}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? "bg-amber-500 text-black" :
                        idx === 1 ? "bg-neutral-400 text-black" :
                        idx === 2 ? "bg-orange-700 text-white" :
                        "bg-neutral-700 text-neutral-300"
                      }`}>
                        {idx === 0 ? <Trophy className="w-4 h-4" /> : leader.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white truncate">{leader.name}</p>
                          {leader.badge_details?.map((badge) => {
                            const BadgeIcon = BADGE_ICONS[badge.icon] || Award;
                            return (
                              <div
                                key={badge.id}
                                className={`w-5 h-5 bg-${badge.color}-500/20 rounded-full flex items-center justify-center`}
                                title={badge.name}
                              >
                                <BadgeIcon className={`w-3 h-3 text-${badge.color}-400`} />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-neutral-400 truncate">{leader.business_description || "Community Member"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#39FF14]">{leader.points}</p>
                        <p className="text-xs text-neutral-500">points</p>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-neutral-500">
                      <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No leaders yet this period</p>
                      <p className="text-sm">Start earning points by posting opportunities, joining pods, and more!</p>
                    </div>
                  )}
                </div>

                {/* How to Earn Points */}
                <div className="pt-4 border-t border-neutral-700">
                  <p className="text-sm font-medium text-neutral-300 mb-3">How to Earn Points</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(leaderboard.points_config || {}).map(([action, points]) => (
                      <div key={action} className="text-center p-2 bg-neutral-900 rounded-lg">
                        <p className="text-lg font-bold text-[#39FF14]">+{points}</p>
                        <p className="text-xs text-neutral-400">{action.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Available Badges */}
                <div className="pt-4 border-t border-neutral-700">
                  <p className="text-sm font-medium text-neutral-300 mb-3">Badges to Earn</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {leaderboard.available_badges?.map((badge) => {
                      const BadgeIcon = BADGE_ICONS[badge.icon] || Award;
                      const hasBadge = leaderboard.my_stats?.badges?.includes(badge.id);
                      return (
                        <div
                          key={badge.id}
                          className={`p-3 rounded-lg border text-center ${
                            hasBadge
                              ? `bg-${badge.color}-500/10 border-${badge.color}-500/30`
                              : "bg-neutral-900 border-neutral-700 opacity-60"
                          }`}
                        >
                          <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                            hasBadge ? `bg-${badge.color}-500/20` : "bg-neutral-800"
                          }`}>
                            <BadgeIcon className={`w-5 h-5 ${hasBadge ? `text-${badge.color}-400` : "text-neutral-500"}`} />
                          </div>
                          <p className={`text-sm font-medium ${hasBadge ? "text-white" : "text-neutral-400"}`}>{badge.name}</p>
                          <p className="text-xs text-neutral-500 mt-1">{badge.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#39FF14]" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Community Activity Dashboard (Consultant Only) */}
      {user?.role === "consultant" && activityDashboard && (
        <Card className="bg-neutral-800/50 border-neutral-700" data-testid="activity-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#39FF14]" />
              Community Activity Dashboard
            </CardTitle>
            <CardDescription className="text-neutral-400">
              Real-time engagement metrics for your community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Total Members", value: activityDashboard.members?.total || 0, icon: Users, color: "emerald" },
                  { label: "Premium Members", value: (activityDashboard.members?.premium_subscribed || 0) + (activityDashboard.members?.clients_with_premium || 0), icon: Crown, color: "amber" },
                  { label: "Open Opportunities", value: activityDashboard.engagement?.open_opportunities || 0, icon: Briefcase, color: "blue" },
                  { label: "Total Resources", value: activityDashboard.engagement?.total_resources || 0, icon: Gift, color: "purple" }
                ].map((stat, idx) => (
                  <div key={idx} className={`p-3 rounded-lg`} style={{background: `rgba(${stat.color === 'emerald' ? '16,185,129' : stat.color === 'amber' ? '245,158,11' : stat.color === 'blue' ? '59,130,246' : '168,85,247'}, 0.1)`, borderColor: `rgba(${stat.color === 'emerald' ? '16,185,129' : stat.color === 'amber' ? '245,158,11' : stat.color === 'blue' ? '59,130,246' : '168,85,247'}, 0.2)`, border: '1px solid'}}>
                    <stat.icon className="w-4 h-4 text-neutral-400 mb-1" />
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-neutral-400">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Engagement Stats */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-[#39FF14]" />
                    Engagement Overview
                  </h4>
                  <div className="space-y-2">
                    {[
                      { label: "Discussion Posts", value: activityDashboard.engagement?.total_discussion_posts || 0, sub: `${activityDashboard.engagement?.new_posts_this_month || 0} this month` },
                      { label: "Connections Made", value: activityDashboard.engagement?.total_connections || 0 },
                      { label: "Messages Sent", value: activityDashboard.engagement?.total_messages || 0, sub: `${activityDashboard.engagement?.messages_this_week || 0} this week` },
                      { label: "Pod Members", value: activityDashboard.pods?.total_pod_members || 0, sub: `across ${activityDashboard.pods?.total_pods || 0} pods` }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5 border-b border-neutral-800 last:border-0">
                        <span className="text-sm text-neutral-400">{item.label}</span>
                        <div className="text-right">
                          <span className="text-sm font-medium text-white">{item.value}</span>
                          {item.sub && <span className="text-xs text-neutral-500 ml-2">{item.sub}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-400" />
                    Resources by Category
                  </h4>
                  <div className="space-y-2">
                    {(activityDashboard.resource_breakdown || []).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5">
                        <span className="text-sm text-neutral-400 capitalize">{item.category}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-neutral-700 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{width: `${Math.min((item.count / Math.max(...(activityDashboard.resource_breakdown || []).map(r => r.count), 1)) * 100, 100)}%`}} />
                          </div>
                          <span className="text-xs font-medium text-white w-6 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Opportunities */}
              {activityDashboard.top_opportunities?.length > 0 && (
                <div className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                  <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    Top Opportunities by Views
                  </h4>
                  <div className="space-y-2">
                    {activityDashboard.top_opportunities.map((opp, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5 border-b border-neutral-800 last:border-0">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Badge variant="outline" className="text-xs border-neutral-600 capitalize shrink-0">{opp.opportunity_type}</Badge>
                          <span className="text-sm text-neutral-300 truncate">{opp.title}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-neutral-500 shrink-0 ml-2">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{opp.views || 0}</span>
                          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{opp.responses || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Membership Breakdown */}
              <div className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  Membership Breakdown
                </h4>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="h-3 bg-neutral-700 rounded-full overflow-hidden flex">
                      {activityDashboard.members?.clients_with_premium > 0 && (
                        <div className="h-full bg-amber-500" style={{width: `${(activityDashboard.members.clients_with_premium / activityDashboard.members.total) * 100}%`}} />
                      )}
                      {activityDashboard.members?.premium_subscribed > 0 && (
                        <div className="h-full bg-[#39FF14]" style={{width: `${(activityDashboard.members.premium_subscribed / activityDashboard.members.total) * 100}%`}} />
                      )}
                      {activityDashboard.members?.free_members > 0 && (
                        <div className="h-full bg-neutral-500" style={{width: `${(activityDashboard.members.free_members / activityDashboard.members.total) * 100}%`}} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Clients ({activityDashboard.members?.clients_with_premium || 0})</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#39FF14]" /> Premium ({activityDashboard.members?.premium_subscribed || 0})</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-neutral-500" /> Free ({activityDashboard.members?.free_members || 0})</span>
                </div>
              </div>

              {/* Weekly Digest */}
              <div className="p-4 bg-neutral-900 rounded-lg border border-neutral-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#39FF14]" />
                    Weekly Community Digest
                  </h4>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await authFetch(`${API}/community/admin/trigger-digest`, { method: "POST" });
                        if (res.ok) {
                          const data = await res.json();
                          if (data.result?.skipped) {
                            // digest already sent
                          } else if (data.result?.sent > 0) {
                            // sent successfully
                          }
                        }
                      } catch (_e) { /* ignore */ }
                    }}
                    className="bg-[#39FF14] text-black hover:bg-[#32e612] text-xs"
                    data-testid="send-digest-btn"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Send Now
                  </Button>
                </div>
                <p className="text-xs text-neutral-500">Auto-sends every Monday at 10 AM to premium members. Includes new opportunities, resources, and discussion highlights from the past week.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function WelcomeVideoCard({ user, API, authFetch }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const isConsultant = user?.role === "consultant";

  useEffect(() => {
    fetch(`${API}/community/welcome-video`)
      .then(r => r.ok ? r.json() : {})
      .then(d => setVideoUrl(d.video_url || ""))
      .catch(() => {});
  }, [API]);

  const handleSaveUrl = async () => {
    if (!newUrl.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/community/welcome-video`, {
        method: "POST",
        body: JSON.stringify({ video_url: newUrl.trim() }),
      });
      if (res.ok) {
        setVideoUrl(newUrl.trim());
        setShowUpload(false);
        toast.success("Welcome video updated");
      }
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <>
      <Card className="bg-gradient-to-br from-emerald-900/30 to-neutral-800 border-emerald-500/20">
        <CardContent className="pt-6">
          <div className="text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_1f6fc830-db93-46be-8373-0fbe94240b47/artifacts/1h0hcmcd_MBC%20logo%20%28400%20x%20400%20px%29.png"
              alt="My Biz Consulting"
              className="w-16 h-16 object-contain mx-auto mb-3"
            />
            <h3 className="text-lg font-semibold text-white mb-1">Welcome to My Biz Consulting</h3>
            <p className="text-sm text-neutral-400 mb-4">
              Watch the welcome video to learn how to get results here
            </p>
            {videoUrl ? (
              <Button className="w-full bg-[#39FF14] text-black hover:bg-[#32e612]" onClick={() => setShowVideo(true)} data-testid="watch-welcome-video-btn">
                <Play className="w-4 h-4 mr-2" /> Watch Welcome Video
              </Button>
            ) : (
              <p className="text-xs text-neutral-500">Video coming soon</p>
            )}
            {isConsultant && (
              <Button variant="ghost" size="sm" className="w-full mt-2 text-neutral-500 hover:text-white" onClick={() => { setNewUrl(videoUrl); setShowUpload(true); }} data-testid="set-video-url-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> {videoUrl ? "Change Video" : "Set Video URL"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Video Player Dialog */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="sm:max-w-2xl bg-black border-neutral-800 p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-white flex items-center justify-between">
              Welcome to My Biz Consulting
              <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-white" onClick={() => setShowVideo(false)}>
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 pt-2">
            <video controls autoPlay className="w-full rounded-lg" src={videoUrl}>
              Your browser does not support video playback.
            </video>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload URL Dialog (consultant only) */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Welcome Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-neutral-600">
              Enter the URL of your welcome video (MP4 file hosted online, YouTube link, or any video URL).
            </p>
            <Input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://example.com/welcome-video.mp4"
              data-testid="video-url-input"
            />
            <Button onClick={handleSaveUrl} disabled={saving || !newUrl.trim()} className="w-full bg-[#333333] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Video URL
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

